import { FC, useRef, useState } from 'react';
import { TieredMenu } from 'primereact/tieredmenu';
import { Button } from 'primereact/button';
import { MenuItem } from 'primereact/menuitem';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { resetUser, selectUser } from '../../redux/reducers/userSlice';
import { useUserActionMutation } from '../../custom-hooks/useUserActionMutation';
import { HeaderAvatarImage } from './HeaderAvatarImage';
import futureSelvesBanner from './assets/future_selves_banner.svg';
import { useTEAME223SpeculativeAvatarData } from './useTEAME223SpeculativeAvatarData';
import FutureSelfModal from './FutureSelfModal';
import { usePostHog } from '@posthog/react';
import { Roles } from '../../models/User';

const AvatarReminderBadge: FC = () => (
  <span aria-hidden="true" className="tw-absolute tw-top-0 tw-right-0 tw-flex tw-h-3 tw-w-3">
    <span
      className="tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-blue-400 tw-opacity-70 tw-animate-ping"
      style={{ animationDuration: '2s' }}
    />
    <span className="tw-relative tw-inline-flex tw-h-3 tw-w-3 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-500 tw-border tw-border-white tw-text-[7px] tw-font-bold tw-leading-none tw-text-white tw-shadow-[0_0_4px_rgba(255,255,255,0.85)]">
      !
    </span>
  </span>
);

// Only mount this component when the TEAME-223 flag is enabled AND the user is a student.
// HeaderAvatarImage (and the Relay hooks it uses) only run when this component is rendered.
const HeaderProfileMenuTeame223: FC = () => {
  const { logout } = useAuth0();
  const dispatch = useAppDispatch();
  const profileMenu = useRef<TieredMenu>(null);
  const [isFutureSelfModalVisible, setIsFutureSelfModalVisible] = useState(false);
  const [toggleButton, setToggleButton] = useState<boolean>(false);
  const userRedux = useAppSelector(selectUser);
  const [commitLoginMutation] = useUserActionMutation();
  const { avatarDataUrl, hasSeenAvatar, hasHydratedFutureSelfData } = useTEAME223SpeculativeAvatarData();
  // only show reminder if user has seen the future selves modal, but does not have an avatar image
  const shouldShowAvatarBadge = hasHydratedFutureSelfData && hasSeenAvatar && !avatarDataUrl;
  const posthog = usePostHog();

  const adminRoles = [Roles.SITE_ADMIN, Roles.NETWORK_ADMIN];
  const rolesArray = [Roles.TEACHER, ...adminRoles];
  const isTeacherOrSiteAdminOrNetworkAdmin =
    userRedux?.roles?.some(role => rolesArray.includes(role?.name as Roles)) ?? false;

  const profileButtonItems = (): MenuItem[] => {
    const logoutButton: MenuItem = {
      label: 'Logout',
      icon: 'nc_icon-nc_close icon-sm',
      command: () => {
        commitLoginMutation({
          variables: {
            input: {
              userAction: {
                userId: `UserNode:${userRedux?.id}`,
                actionTypeId: 'ActionTypeNode:2',
                timeStamp: new Date().toISOString(),
                description: 'logout',
              },
            },
          },
        });
        dispatch(resetUser());
        posthog.reset();
        logout({ logoutParams: { returnTo: window.location.origin } });
      },
    };

    const generateAvatarButton: MenuItem = {
      template: () => (
        <button
          type="button"
          className="tw-relative tw-w-full tw-border-none tw-cursor-pointer tw-rounded-xl tw-overflow-hidden tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-flex tw-flex-col tw-items-center tw-gap-2 tw-p-1"
          onClick={() => {
            // allow users to re-open the future selves modal if they have not yet created an avatar
            setIsFutureSelfModalVisible(true);
          }}
        >
          <img
            src={futureSelvesBanner}
            alt=""
            aria-hidden="true"
            className="tw-w-full tw-max-h-17 tw-object-cover tw-rounded-lg"
          />
          <span className="tw-text-sm tw-font-semibold">Make a Future Self Avatar</span>
          {shouldShowAvatarBadge && <AvatarReminderBadge />}
        </button>
      ),
    };

    return avatarDataUrl ? [logoutButton] : [generateAvatarButton, logoutButton];
  };

  const profileA11yLabel = `${userRedux?.name ?? 'User'} user profile menu`;
  const profileAvatarIcon = (
    // compose appropriate header avatar either with image or notification
    <div className="tw-relative tw-inline-flex">
      <HeaderAvatarImage fallback={userRedux?.picture} />
      {shouldShowAvatarBadge && <AvatarReminderBadge />}
    </div>
  );

  return (
    <>
      <FutureSelfModal isVisible={isFutureSelfModalVisible} onVisibleChange={setIsFutureSelfModalVisible} />
      <div data-testid="profile_menu">
        <TieredMenu
          model={profileButtonItems()}
          popup
          ref={profileMenu}
          onShow={() => setToggleButton(true)}
          onHide={() => setToggleButton(false)}
          id="profile_menu"
          pt={{
            menuitem: {
              'aria-level': undefined,
            },
          }}
        />
        <Button
          data-hj-suppress
          label={userRedux?.name}
          icon={profileAvatarIcon}
          rounded
          outlined
          onClick={event => profileMenu?.current?.toggle(event)}
          aria-label={profileA11yLabel}
          aria-haspopup="menu"
          aria-controls="profile_menu"
          aria-expanded={toggleButton}
          className={`lg:flex hidden ${isTeacherOrSiteAdminOrNetworkAdmin ? '' : 'ph-no-capture'}`}
        />
        <Button
          data-hj-suppress
          icon={profileAvatarIcon}
          rounded
          outlined
          onClick={event => profileMenu?.current?.toggle(event)}
          aria-label={profileA11yLabel}
          aria-haspopup="menu"
          aria-controls="profile_menu"
          aria-expanded={toggleButton}
          className={`lg:hidden flex p-0 justify-content-center align-items-center ${
            isTeacherOrSiteAdminOrNetworkAdmin ? '' : 'ph-no-capture'
          }`}
        />
      </div>
    </>
  );
};

export default HeaderProfileMenuTeame223;
